import { useCallback, useMemo } from "react";

import { useLocalStorage } from "@/hooks/common/useLocalStorage";
import { EMPTY_LEAGUE_SETUP_FILTER_VALUE, LeagueSetupFilterValue } from "@/types";

/**
 * localStorage key family for the per-league persisted global filter selection.
 * `useLocalStorage` prepends the `tournated:` app prefix, so the real stored keys
 * look like `tournated:league-global-filter:v1:<leagueId>` — one per league.
 */
const STORAGE_KEY_FAMILY = "league-global-filter:";

/**
 * Versioned localStorage key for a league's persisted global filter selection.
 * Keyed by league public id so each league remembers its own default.
 */
const buildStorageKey = (leagueId: string) => `${STORAGE_KEY_FAMILY}v1:${leagueId}`;

/**
 * Wipes EVERY league's persisted global filter selection (one localStorage key
 * per league in the `league-global-filter:` family). Call on logout / 401 so one
 * user's league defaults can't leak into the next login on the same browser.
 * Mirrors {@link clearCurrentLeagueId} / `clearRecentSearchItems`.
 */
export const clearAllLeagueGlobalFilters = () => {
  // `useLocalStorage` writes under the `tournated:` prefix, so match against the
  // fully-prefixed family name when scanning raw localStorage keys.
  const prefixedFamily = `tournated:${STORAGE_KEY_FAMILY}`;
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(prefixedFamily)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // localStorage may be unavailable (private mode, quota) — nothing to clear.
  }
};

/** Coerce a possibly-partial persisted value into a complete selection. */
const normalize = (value: Partial<LeagueSetupFilterValue> | null): LeagueSetupFilterValue => ({
  seasonIds: value?.seasonIds?.slice(0, 1) ?? [],
  regionIds: value?.regionIds ?? [],
  levelIds: value?.levelIds ?? [],
  customTagIds: value?.customTagIds ?? [],
});

/**
 * The league's GLOBAL filter selection (one season / regions / levels / custom tags),
 * persisted to localStorage keyed by league id. This is the "default" surface
 * edited on the LeaguePage filter bar. Other pages read `value` as their filter
 * DEFAULT only (page-level changes live in the URL and must NEVER write back
 * here) — mirroring the platform/organization "global = default, page overrides
 * live in URL" pattern.
 *
 * When no league is selected the value is a stable empty selection and `setValue`
 * is a no-op.
 */
export const useLeagueGlobalFilter = (
  leagueId: string | null
): { value: LeagueSetupFilterValue; setValue: (next: LeagueSetupFilterValue) => void } => {
  const [stored, setStored] = useLocalStorage<Partial<LeagueSetupFilterValue> | null>(
    buildStorageKey(leagueId ?? "__none__"),
    null
  );

  const value = useMemo<LeagueSetupFilterValue>(
    () => (leagueId ? normalize(stored) : EMPTY_LEAGUE_SETUP_FILTER_VALUE),
    [leagueId, stored]
  );

  const setValue = useCallback(
    (next: LeagueSetupFilterValue) => {
      if (!leagueId) return;
      setStored(normalize(next));
    },
    [leagueId, setStored]
  );

  return { value, setValue };
};
